import { useState, useEffect, useCallback } from "react";
import type { AssistantApi } from "~/components/assistant/api/AssistantApi";
import type { MCPServer } from "~/components/assistant/types";
import { useUserPreferences, type McpToolPrefsEntry } from "~/hooks/useUserPreferences";
import { trackEvent } from "~/utils/monitoring";

/**
 * Filters the server list so that external servers not in the enabled set are hidden.
 * Internal servers always pass through. Uses the is_external_mcp flag from the backend.
 */
export function filterVisibleServers(
  servers: MCPServer[],
  externalMcpAccessIds: string[]
): MCPServer[] {
  const enabledSet = new Set(externalMcpAccessIds);
  return servers.filter((s) => !s.is_external_mcp || enabledSet.has(s.id));
}

/**
 * Resolves which servers should be selected given fetched servers and saved preferences.
 * - Saved selections are filtered to only IDs still present in fetched servers.
 * - Required servers are always included.
 * - Newly added servers (not in saved `available`) use their `default` value.
 * - Previously available servers respect the user's saved choice.
 */
export function resolveSelectedServers(servers: MCPServer[], saved: McpToolPrefsEntry): string[] {
  const fetchedIds = new Set(servers.map((s) => s.id));
  const previouslyAvailable = new Set(saved.available);

  const selected = new Set<string>();

  // Start with saved selections, filtered to still-existing servers
  for (const id of saved.selected) {
    if (fetchedIds.has(id)) {
      selected.add(id);
    }
  }

  for (const server of servers) {
    // Required servers are always on
    if (server.required) {
      selected.add(server.id);
    }
    // New servers (not previously available) use their default
    if (!previouslyAvailable.has(server.id) && server.default) {
      selected.add(server.id);
    }
  }

  return Array.from(selected);
}

/**
 * Manages MCP server state: fetching the server list, restoring/persisting
 * per-context toggle preferences via useUserPreferences, and toggling servers.
 */
export function useMcpServers(api: AssistantApi, contextId: string) {
  const [mcpServers, setMcpServers] = useState<MCPServer[]>([]);
  const [selectedMcpServers, setSelectedMcpServers] = useState<string[]>([]);
  const [preferences, updatePreferences] = useUserPreferences();

  // Load MCP servers on mount
  useEffect(() => {
    let cancelled = false;

    api
      .listMcpServers()
      .then((servers) => {
        if (cancelled) {
          return;
        }
        setMcpServers(servers);

        // Restore saved preferences if available, otherwise use defaults
        const saved = contextId ? (preferences.mcpToolPreferences?.[contextId] ?? null) : null;
        const selectedIds = saved
          ? resolveSelectedServers(servers, saved)
          : servers.filter((s) => s.default).map((s) => s.id);
        setSelectedMcpServers(selectedIds);
      })
      .catch((error) => {
        console.error("Failed to load MCP servers:", error);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleMcpServer = useCallback(
    (serverId: string) => {
      setSelectedMcpServers((prev) => {
        const isCurrentlySelected = prev.includes(serverId);
        const newSelection = isCurrentlySelected
          ? prev.filter((id) => id !== serverId)
          : [...prev, serverId];

        const server = mcpServers.find((s) => s.id === serverId);

        trackEvent("analysis_agent_mcp_server_toggled", {
          dataset_id: contextId || "Unknown",
          context: "dataset",
          server_id: serverId,
          server_name: server?.name || serverId,
          enabled: !isCurrentlySelected,
          total_selected: newSelection.length,
        });

        // Persist preference so it survives refresh
        if (contextId) {
          const availableIds = mcpServers.map((s) => s.id);
          updatePreferences({
            mcpToolPreferences: {
              ...preferences.mcpToolPreferences,
              [contextId]: { selected: newSelection, available: availableIds },
            },
          });
        }

        return newSelection;
      });
    },
    [mcpServers, contextId, preferences.mcpToolPreferences, updatePreferences]
  );

  return { mcpServers, selectedMcpServers, toggleMcpServer };
}
